import { MutableRefObject, RefObject, useEffect, useLayoutEffect, useRef } from 'react'
import useLatest from 'src/hooks/useLatest'

const gapAboveUnreadDivider = 16

type FeedOpenPositionOptions = {
  /** The scrolling box. */
  areaRef: RefObject<HTMLElement>
  /** End-of-feed marker, positioned on when there is nothing unread. */
  newestRef: RefObject<HTMLElement>
  /** Anchor on the first unread item, positioned on when the feed has one. */
  firstUnreadRef?: RefObject<HTMLElement>
  /**
   * Changes whenever the unread boundary moves, so the feed is re-positioned: the marker
   * resolves after the items do, which moves the anchor without changing them.
   * `undefined` means it has not resolved yet — positioning waits, rather than going to
   * the newest message and then jumping to the unread one a moment later. `null` means it
   * resolved to no boundary at all, which is an answer: position on the newest message.
   */
  unreadBoundaryKey?: number | null
  itemCount: number
  /**
   * A feed loads in several passes — cache, then network, then newer messages — and each
   * pass moves the target, so the feed is re-positioned on all three rather than latching
   * onto the first one.
   */
  oldestItemID?: string
  newestItemID?: string
  /** Once the feed has been scrolled by hand, positioning stops happening to it. */
  hasScrolledManually: MutableRefObject<boolean>
  /** Stop following the newest item, so a position on the unread one is not pulled down. */
  stopFollowing: () => void
}

/**
 * Positions a feed on its first unread item when it opens, so unread messages are read
 * top-down and the newest are a scroll away (like Slack); otherwise on the newest item.
 */
export default function useFeedOpenPosition({
  areaRef,
  newestRef,
  firstUnreadRef,
  unreadBoundaryKey,
  itemCount,
  oldestItemID,
  newestItemID,
  hasScrolledManually,
  stopFollowing,
}: FeedOpenPositionOptions) {
  const positionedAt = useRef<'unread' | 'newest' | null>(null)

  const positionFeed = () => {
    const { current: area } = areaRef
    if (hasScrolledManually.current || itemCount === 0 || !area || unreadBoundaryKey === undefined) {
      return
    }
    const firstUnread = firstUnreadRef?.current
    if (firstUnread?.scrollIntoView) {
      positionedAt.current = 'unread'
      firstUnread.scrollIntoView({ behavior: 'auto', block: 'start' })
      // Stop just above the divider rather than exactly on it: at the boundary it sticks
      // to the top and collapses into its "scrolled past" pill, which reads as the unread
      // messages being above the viewport.
      area.scrollTop -= gapAboveUnreadDivider
      stopFollowing()
      return
    }
    if (positionedAt.current === 'unread') {
      // The feed had a boundary and lost it (it just got marked read). Leave the viewport
      // where it is rather than yanking it to the newest message.
      return
    }
    positionedAt.current = 'newest'
    // block: 'start' (the default) overshoots and clamps, stopping exactly at the bottom
    // including the container's padding — 'end' would stop short of it.
    newestRef.current?.scrollIntoView?.({ behavior: 'auto' })
  }
  // Read through a ref so the observer below always runs the current render's version.
  const positionFeedRef = useLatest(positionFeed)

  // A layout effect, so positioning happens before the feed is painted.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(positionFeed, [itemCount, oldestItemID, newestItemID, unreadBoundaryKey])

  // Message heights settle over a few frames after they render, which moves the target out
  // from under a position that has already been applied — leaving the feed short of the
  // newest message, or off the unread one. Re-position until it is scrolled by hand.
  useEffect(() => {
    const { current: area } = areaRef
    if (!area || typeof ResizeObserver === 'undefined') {
      return undefined
    }
    const observer = new ResizeObserver(() => positionFeedRef.current())
    Array.from(area.children).forEach((child) => observer.observe(child))
    return () => observer.disconnect()
  }, [areaRef, itemCount, positionFeedRef])
}
